import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon, { AssetTypes } from "../../../../../../../public/icons-v1_1";
import PathPickBasketActionIconBox from "@/features/pathpick/features/basket/components/action-panel/PathPickBasketActionIconBox";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { DemoRegistrationStatus } from "@/features/pathpick/features/start-guide/hooks/useStartGuideDemoState";
import { StartGuideSalesMode } from "@/features/pathpick/features/start-guide/types";

interface Props {
  // i18n 블록 이름 (workbench.api / workbench.file / workbench.kream)
  variant: StartGuideSalesMode;
  icon: AssetTypes;
  status: DemoRegistrationStatus;
  isDisabled: boolean;
  disabledHintId?: string;
  // 완료 문구에 들어갈 실제 처리 상품 수.
  selectedCount?: number;
  // 완료 문구 아래 한 줄 더 붙는 안내 (이미 번역된 문자열). 없으면 완료 문구만 남는다.
  doneDescription?: string;
  onRun: () => void;
  // 설명과 CTA 사이에 끼는 보조 정보 (선택 상품 수)
  meta?: React.ReactNode;
  // 패널이 실행 버튼을 갖는지 — 끄면 설명만 남고 이동은 화면 상단 CTA가 맡는다.
  hasAction?: boolean;
  // 완료 후 추가로 붙는 액션 (API 흐름의 등록 예시 보기)
  footer?: React.ReactNode;
}

/**
 * 담은 상품 작업 패널 — 판매 방식에 따라 문구와 CTA만 달라진다.
 *
 * 운영 담은 상품 화면의 패널 행(아이콘 박스 + 제목/설명 + 184px CTA)과 같은 모양을 쓰되,
 * 그쪽 패널은 props 없이 훅으로만 구동되고 사용량 카드를 품고 있어 그대로 재사용할 수 없다.
 * 전달서 §10 이 사용량 표시를 금지하므로 여기서는 아이콘 박스만 가져다 쓴다.
 */
function PathPickStartGuideActionPanel({
  variant,
  icon,
  status,
  isDisabled,
  disabledHintId,
  selectedCount,
  doneDescription,
  onRun,
  meta,
  hasAction = true,
  footer,
}: Props) {
  const { t } = useI18nHelper();
  const prefix = `path_pick_guide:workbench.${variant}`;
  const isDone = status === "done";

  return (
    <Flex
      flexDirection={"column"}
      gap={"12px"}
      padding={"16px"}
      borderRadius={"8px"}
      border={"0.5px solid #D4D4D1"}
      backgroundColor={"#FFFFFF"}
    >
      <Flex gap={"12px"} alignItems={"flex-start"}>
        <PathPickBasketActionIconBox src={icon} icoSize={24} />
        <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
          <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
            {t(`${prefix}.title`)}
          </Text>
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
            {t(`${prefix}.description`)}
          </Text>
        </Flex>
      </Flex>

      {meta}

      {!hasAction ? null : isDone ? (
        // 완료 안내는 색만으로 구분하지 않고 문구로도 드러낸다(전달서 §22).
        <Flex
          role={"status"}
          gap={"6px"}
          alignItems={"flex-start"}
          padding={"10px 12px"}
          borderRadius={"6px"}
          border={"0.5px solid #A9E0AB"}
          backgroundColor={"#E9F7EF"}
          color={"#247A52"}
        >
          {/* 안내가 두 줄로 늘어나도 아이콘은 첫 줄에 붙어 있어야 한다. */}
          <Flex flexShrink={0} paddingTop={"1px"}>
            <CustomIcon src={"check"} width={16} height={16} />
          </Flex>
          <Flex flexDirection={"column"} gap={"4px"} minWidth={0}>
            <Text textStyle={"body-sm-bold"} color={"#247A52"}>
              {t(`${prefix}.done_panel`, { selected: selectedCount })}
            </Text>
            {doneDescription && (
              <Text textStyle={"body-sm-medium"} color={"#247A52"}>
                {doneDescription}
              </Text>
            )}
          </Flex>
        </Flex>
      ) : (
        <AppButton
          variant={"primary"}
          width={"100%"}
          height={"36px"}
          isDisabled={isDisabled}
          isLoading={status === "processing"}
          onClick={onRun}
          chakraProps={
            isDisabled && disabledHintId
              ? { "aria-describedby": disabledHintId }
              : undefined
          }
        >
          {t(
            status === "processing" ? `${prefix}.processing` : `${prefix}.cta`
          )}
        </AppButton>
      )}

      {footer}
    </Flex>
  );
}

export default PathPickStartGuideActionPanel;
